import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { PaymentReminders } from "./PaymentReminders";
import { TasksSection } from "./TasksSection";
import { ChecklistSection } from "./ChecklistSection";
import VisitorsReminders from "./VisitorsReminders";
import FeedbackRemainders from "./FeedbackRemainders";
import TitleWithAddButton from "../Home/TitleWithAddButton";


import StorageFactory from "../../Util/StorageFactory";
import {
  ADD,
  ROUTE_ADD_ENQUIRY,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
} from "../../Util/VMConstants";

import { API_GET_TODAY_UPDATES } from "../../Util/URLConstants";
import { checkIsPermissionExist, isEmpty } from "../../Util/UtilHelper";
import MUIBox from "../../Components/MUIBox";
import MainAnalytics from "./MainAnalytics";
import { FCM_HOME_REFRESH_EVENT } from "../../Util/FCM/notificationRoutes";

const VMMainHome = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [eventsCount, setEventsCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [checklists, setCheckLists] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [feedback, setFeedback] = useState([]);

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);

  const todayDate = dayjs().format("YYYY-MM-DD");

  const canViewEnquiries = checkIsPermissionExist(VM_PERMISSIONS, "VM002");
  const canViewAppointments = checkIsPermissionExist(VM_PERMISSIONS, "VM010");
  const canViewTasks = checkIsPermissionExist(VM_PERMISSIONS, "VM029");
  const canViewChecklists = checkIsPermissionExist(VM_PERMISSIONS, "VM032");
  const canViewInvoices = checkIsPermissionExist(VM_PERMISSIONS, "VM006");
  const canViewVisitors = checkIsPermissionExist(VM_PERMISSIONS, "VM025");
  const canViewFeedback = checkIsPermissionExist(VM_PERMISSIONS, "VM027");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getTodayUpdates = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await axios.get(
        `${API_GET_TODAY_UPDATES}?user_id=${userID}`
      );

      if (resp.data?.status_code === 200) {
        const res = resp.data.data;
        const booked = res.booked_enquiries || [];
        setEnquiries(res.enquiries || []);
        const uniqueEventEnquiries = new Set(
          booked.map((item) => item.enquiry_id).filter(Boolean)
        ).size;
        setEventsCount(
          res.events_count != null && res.events_count !== ""
            ? Number(res.events_count)
            : uniqueEventEnquiries
        );
        setAppointments(res.event_appointments || []);
        setFollowups(res.event_followups || []);
        setTasks(res.tasks || []);
        setCheckLists(res.checklists || []);
        setInvoices(res.invoices || []);
        setVisitors(res.visitors || []);
        setFeedback(res.feedback || []);
      }
    } catch (err) {
      console.error("Error fetching today updates:", err);
    } finally {
      setLoading(false);
    }
  }, [userID]);

  useEffect(() => {
    if (!isEmpty(userID)) {
      getTodayUpdates();
    }
  }, [userID, getTodayUpdates]);

  useEffect(() => {
    if (isEmpty(userID)) return undefined;

    const onHomeRefresh = () => {
      getTodayUpdates();
    };
    window.addEventListener(FCM_HOME_REFRESH_EVENT, onHomeRefresh);
    return () => {
      window.removeEventListener(FCM_HOME_REFRESH_EVENT, onHomeRefresh);
    };
  }, [userID, getTodayUpdates]);

  return (
    <MUIBox
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8F8F8",
        // px: { xs: 1.5, sm: 2, md: 3 },
        // py: { xs: 2, sm: 3 },
      }}
    >
      <MUIBox sx={{ maxWidth: 1400, mx: "auto", position: "relative" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.6)",
              zIndex: 9,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Header */}
        <TitleWithAddButton
          title="Home"
          onAddClick={() =>
            navigate("/" + ROUTE_ADD_ENQUIRY, {
              state: { arrived: ADD },
            })
          }
          buttonLabel="Add Enquiry"
          isButtonDisplay={checkIsPermissionExist(VM_PERMISSIONS, "VM001")}
        />
  
        {(canViewEnquiries || canViewAppointments) && (
          <Box
            sx={{
              overflowX: "auto",
              mb: 3,
            }}
          >
            <MainAnalytics
              enquiries={enquiries}
              todayDate={todayDate}
              eventsCount={eventsCount}
              appointments={appointments}
              followups={followups}
              canViewEnquiries={canViewEnquiries}
              canViewAppointments={canViewAppointments}
            />
          </Box>
        )}

        {(canViewChecklists || canViewTasks) && (
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md:
                  canViewChecklists && canViewTasks ? "1fr 1fr" : "1fr",
              },
              gap: 2,
              mb: 3,
              overflowX: "auto",
            }}
          >
            {canViewChecklists && <ChecklistSection data={checklists} />}
            {canViewTasks && <TasksSection data={tasks} />}
          </Box>
        )}

        {canViewInvoices && (
          <Box
            sx={{
              overflowX: "auto",
              mb: 3,
            }}
          >
            <PaymentReminders data={invoices} />
          </Box>
        )}

        {(canViewVisitors || canViewFeedback) && (
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: canViewVisitors && canViewFeedback ? "1fr 1fr" : "1fr",
              },
              gap: 2,
              mb: 3,
              mt: 2,
              overflowX: "auto",
            }}
          >
            {canViewVisitors && <VisitorsReminders data={visitors} />}
            {canViewFeedback && <FeedbackRemainders data={feedback} />}
          </Box>
        )}
      </MUIBox>
    </MUIBox>
  );
};

export default VMMainHome;
